// ============================================================================
// Layout comum das páginas públicas da Base de Conhecimento (F3 Bloco A)
// Renderiza header minimal com nome do tenant + breadcrumb + conteúdo central.
// Não usa sidebar administrativa — visitante anônimo.
//
// CHANGELOG:
//   RELEASE: 2026-07-09 v1.1.0 - Passa a bloquear renderização quando o
//            tenant tem knowledge_enabled = false (preparação add-on Wiki).
// ============================================================================

import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { BookOpen, ChevronRight, Lock } from "lucide-react";
import { useKbPublicModuleEnabled } from "@/hooks/kb/useKbPublicModuleEnabled";

export interface KbPublicBreadcrumbItem {
  label: string;
  to?: string;
}

interface Props {
  tenantSlug: string;
  tenantName?: string;
  breadcrumbs?: KbPublicBreadcrumbItem[];
  children: React.ReactNode;
}

/**
 * Shell minimal para a wiki pública.
 * Sem dependência de contexto autenticado: pode renderizar para visitantes anônimos.
 */
export function KbPublicLayout({ tenantSlug, tenantName, breadcrumbs, children }: Props) {
  const displayName = tenantName ?? tenantSlug;

  // Gate de módulo (add-on Wiki). Enquanto carrega assume habilitado para
  // não piscar tela de bloqueio em conexões lentas.
  const { data: moduleEnabled = true } = useKbPublicModuleEnabled(tenantSlug);

  if (!moduleEnabled) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <Helmet>
          <title>Base de Conhecimento indisponível</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <div className="max-w-md w-full rounded-lg border border-border bg-card p-8 text-center shadow-sm">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
            <Lock className="h-6 w-6 text-muted-foreground" aria-hidden />
          </div>
          <h1 className="text-xl font-semibold text-foreground">
            Base de Conhecimento indisponível
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Este conteúdo está temporariamente inacessível. Tente novamente em breve.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">

      {/* Header público minimal */}
      <header className="sticky top-0 z-40 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
          <Link
            to={`/kb/${tenantSlug}`}
            className="flex items-center gap-2 font-semibold hover:opacity-80"
          >
            <BookOpen className="h-5 w-5 text-primary" aria-hidden />
            <span>{displayName}</span>
            <span className="text-muted-foreground text-sm font-normal">
              · Base de Conhecimento
            </span>
          </Link>
        </div>
        {breadcrumbs && breadcrumbs.length > 0 && (
          <nav
            aria-label="Breadcrumb"
            className="mx-auto max-w-6xl px-4 pb-2 text-sm text-muted-foreground"
          >
            <ol className="flex flex-wrap items-center gap-1">
              {breadcrumbs.map((b, i) => (
                <li key={i} className="flex items-center gap-1">
                  {i > 0 && <ChevronRight className="h-3 w-3" aria-hidden />}
                  {b.to ? (
                    <Link to={b.to} className="hover:text-foreground">
                      {b.label}
                    </Link>
                  ) : (
                    <span className="text-foreground">{b.label}</span>
                  )}
                </li>
              ))}
            </ol>
          </nav>
        )}
      </header>

      <main className="mx-auto max-w-6xl px-4 py-8">{children}</main>

      <footer className="border-t border-border bg-muted/30 py-6 text-center text-sm text-muted-foreground">
        <div className="mx-auto max-w-6xl px-4">
          Conteúdo publicado por{" "}
          <span className="font-medium text-foreground">{displayName}</span>
        </div>
      </footer>
    </div>
  );
}
